/*!
 * sha1.js - SHA1 implementation for bcrypto
 * Copyright (c) 2017-2018, Christopher Jeffrey (MIT License).
 * https://github.com/bcoin-org/bcrypto
 * Parts of this software based on hash.js.
 */

/* eslint camelcase: "off" */
'use strict';

var _classCallCheck = require("/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/classCallCheck");

var _createClass = require("/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/createClass");

var assert = require('bsert');

var HMAC = require('../internal/hmac');
/*
 * Constants
 */


var FINALIZED = -1;
var DESC = Buffer.alloc(8, 0x00);
var PADDING = Buffer.alloc(64, 0x00);
PADDING[0] = 0x80;
var K = new Uint32Array([0x5a827999, 0x6ed9eba1, 0x8f1bbcdc, 0xca62c1d6]);
/**
 * SHA1
 */

var SHA1 = /*#__PURE__*/function () {
  /**
   * Create a SHA1 context.
   * @constructor
   */
  function SHA1() {
    _classCallCheck(this, SHA1);

    this.state = new Uint32Array(5);
    this.msg = new Uint32Array(80);
    this.block = Buffer.allocUnsafe(64);
    this.size = FINALIZED;
  }
  /**
   * Initialize SHA1 context.
   * @returns {SHA1}
   */


  _createClass(SHA1, [{
    key: "init",
    value: function init() {
      this.state[0] = 0x67452301;
      this.state[1] = 0xefcdab89;
      this.state[2] = 0x98badcfe;
      this.state[3] = 0x10325476;
      this.state[4] = 0xc3d2e1f0;
      this.size = 0;
      return this;
    }
    /**
     * Update SHA1 context.
     * @param {Buffer} data
     * @returns {SHA1}
     */

  }, {
    key: "update",
    value: function update(data) {
      assert(Buffer.isBuffer(data));

      this._update(data, data.length);

      return this;
    }
    /**
     * Finalize SHA1 context.
     * @returns {Buffer}
     */

  }, {
    key: "final",
    value: function final() {
      return this._final(Buffer.allocUnsafe(20));
    }
    /**
     * Update SHA1 context.
     * @private
     * @param {Buffer} data
     * @param {Number} len
     */

  }, {
    key: "_update",
    value: function _update(data, len) {
      assert(this.size !== FINALIZED, 'Context already finalized.');
      var pos = this.size & 0x3f;
      var off = 0;
      this.size += len;

      if (pos > 0) {
        var want = 64 - pos;
        if (want > len) want = len;
        data.copy(this.block, pos, off, off + want);
        pos += want;
        len -= want;
        off += want;
        if (pos < 64) return;
        this.transform(this.block, 0);
      }

      while (len >= 64) {
        this.transform(data, off);
        off += 64;
        len -= 64;
      }

      if (len > 0) data.copy(this.block, 0, off, off + len);
    }
    /**
     * Finalize SHA1 context.
     * @private
     * @param {Buffer} out
     * @returns {Buffer}
     */

  }, {
    key: "_final",
    value: function _final(out) {
      assert(this.size !== FINALIZED, 'Context already finalized.');
      var pos = this.size % 64;
      var len = this.size * 8;
      writeU32(DESC, len * (1 / 0x100000000), 0);
      writeU32(DESC, len, 4);

      this._update(PADDING, 1 + (119 - pos) % 64);

      this._update(DESC, 8);

      for (var i = 0; i < 5; i++) {
        writeU32(out, this.state[i], i * 4);
        this.state[i] = 0;
      }

      for (var _i = 0; _i < 80; _i++) {
        this.msg[_i] = 0;
      }

      for (var _i2 = 0; _i2 < 64; _i2++) {
        this.block[_i2] = 0;
      }

      this.size = FINALIZED;
      return out;
    }
    /**
     * Transform SHA1 block.
     * @param {Buffer} chunk
     * @param {Number} pos
     */

  }, {
    key: "transform",
    value: function transform(chunk, pos) {
      var W = this.msg;
      var a = this.state[0];
      var b = this.state[1];
      var c = this.state[2];
      var d = this.state[3];
      var e = this.state[4];
      var i = 0;

      for (; i < 16; i++) {
        W[i] = readU32(chunk, pos + i * 4);
      }

      for (; i < 80; i++) {
        W[i] = rotl32(W[i - 3] ^ W[i - 8] ^ W[i - 14] ^ W[i - 16], 1);
      }

      for (i = 0; i < 80; i++) {
        var s = i / 20 | 0;
        var t = rotl32(a, 5);
        t += ft_1(s, b, c, d);
        t += e;
        t += W[i];
        t += K[s];
        e = d;
        d = c;
        c = rotl32(b, 30);
        b = a;
        a = t;
      }

      this.state[0] += a;
      this.state[1] += b;
      this.state[2] += c;
      this.state[3] += d;
      this.state[4] += e;
    }
  }], [{
    key: "hash",
    value: function hash() {
      return new SHA1();
    }
  }, {
    key: "hmac",
    value: function hmac() {
      return new HMAC(SHA1, 64);
    }
  }, {
    key: "digest",
    value: function digest(data) {
      return SHA1.ctx.init().update(data).final();
    }
  }, {
    key: "root",
    value: function root(left, right) {
      assert(Buffer.isBuffer(left) && left.length === 20);
      assert(Buffer.isBuffer(right) && right.length === 20);
      return SHA1.ctx.init().update(left).update(right).final();
    }
  }, {
    key: "multi",
    value: function multi(x, y, z) {
      var ctx = SHA1.ctx;
      ctx.init();
      ctx.update(x);
      ctx.update(y);
      if (z) ctx.update(z);
      return ctx.final();
    }
  }, {
    key: "mac",
    value: function mac(data, key) {
      return SHA1.hmac().init(key).update(data).final();
    }
  }]);

  return SHA1;
}();

SHA1.native = 0;
SHA1.id = 'SHA1';
SHA1.size = 20;
SHA1.bits = 160;
SHA1.blockSize = 64;
SHA1.zero = Buffer.alloc(20, 0x00);
SHA1.ctx = new SHA1();
/*
 * Helpers
 */

function rotl32(w, b) {
  return w << b | w >>> 32 - b;
}

function ft_1(s, x, y, z) {
  if (s === 0) return ch32(x, y, z);
  if (s === 1 || s === 3) return p32(x, y, z);
  if (s === 2) return maj32(x, y, z);
  return 0;
}

function ch32(x, y, z) {
  return x & y ^ ~x & z;
}

function maj32(x, y, z) {
  return x & y ^ x & z ^ y & z;
}

function p32(x, y, z) {
  return x ^ y ^ z;
}

function writeU32(buf, value, offset) {
  buf[offset] = value >>> 24;
  buf[offset + 1] = value >> 16 & 0xff;
  buf[offset + 2] = value >> 8 & 0xff;
  buf[offset + 3] = value & 0xff;
}

function readU32(buf, offset) {
  return (buf[offset] & 0xff) * 0x1000000 + ((buf[offset + 1] & 0xff) << 16 | (buf[offset + 2] & 0xff) << 8 | buf[offset + 3] & 0xff);
}
/*
 * Expose
 */


module.exports = SHA1;